import { FeatureFlag } from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { QueryCacheKey } from "../../api/queryClient";

import { serializeThemeRef } from "./themeChipRef";
import {
  clearThemeRequestDraft,
  readThemeRequestDraft,
  writeThemeRequestDraft,
} from "./themeRequestDraft";

interface ThemeTrio {
  themeId: string;
  mode: string;
  background: string;
}

interface Params {
  /** Scopes the session-persisted request-form draft. */
  dashboardId?: string;
  openChatSidebar: () => void;
  /** The live theme trio, snapshotted before a chat preview so Keep current can restore it. */
  liveTheme: ThemeTrio;
  /** Re-theme the dashboard locally without persisting (the selector's browse path). */
  previewTheme: (next: Partial<ThemeTrio>) => void;
  /**
   * Persist a theme change (handleThemeChange). Ref-based because the persist callback is
   * declared after this hook runs in the viewer.
   */
  persistThemeRef: React.MutableRefObject<(next: Partial<ThemeTrio>) => void>;
}

/**
 * The chat-driven custom-theme surface of WebDashboardViewer (ENG-7537), extracted so the viewer
 * doesn't accumulate a dozen loose useStates: the in-chat request form (open/remount/focus), the
 * form→editor-thread handoff message, the kebab-Edit composer prefill, and the result card's
 * preview / apply / keep-current flow.
 */
export const useChatThemeControls = ({
  dashboardId,
  openChatSidebar,
  liveTheme,
  previewTheme,
  persistThemeRef,
}: Params) => {
  const queryClient = useQueryClient();

  // Composer prefill (kebab "Edit" on a custom theme, and other callers). The optional context part
  // rides along for the agent but is never shown in the composer.
  const [chatPrefillMessage, setChatPrefillMessage] = useState<string | undefined>(undefined);
  const [chatPrefillContext, setChatPrefillContext] = useState<string | undefined>(undefined);
  // Theme-request handoff: auto-submitted to the editor thread as soon as chat is idle.
  const [pendingThemeChatMessage, setPendingThemeChatMessage] = useState<string | undefined>(
    undefined
  );
  // The in-chat theme request card, opened from the theme selector's "+ Custom" entry. The open
  // flag is session-persisted so a page refresh doesn't drop the form (see themeRequestDraft).
  const [isThemeRequestOpen, setIsThemeRequestOpen] = useState(
    () => readThemeRequestDraft(dashboardId)?.open === true
  );
  // Remounts the card to a blank form; only bumped when the previous one was already submitted.
  const [themeRequestInstanceId, setThemeRequestInstanceId] = useState(0);
  // Bumped on every "+ Custom" click so an already-open card scrolls into view and takes focus.
  const [themeRequestFocusNonce, setThemeRequestFocusNonce] = useState(0);
  const themeRequestWasSubmittedRef = useRef(false);
  // Agent-driven custom theme creation (card→chat handoff, Edit action, result cards).
  // Off = the request card files a custom-theme request as before; rendering custom themes
  // is never gated.
  const { value: isCustomThemeAgentEnabled } = useFeatureGate(
    FeatureFlag.EnableWebDashboardCustomThemeCreation
  );

  // Also serves non-theme callers (e.g. the personalized-views nav prompt) — the composer has a
  // single prefill slot either way.
  const prefillComposer = useCallback((message: string, hiddenContext?: string) => {
    setChatPrefillMessage(message);
    setChatPrefillContext(hiddenContext);
  }, []);

  // Kebab "Edit" on a custom theme: open the chat prefilled with the theme as a chip, caret at the
  // end; the user completes the sentence with what to change (the agent updates the theme in
  // place). The chip carries the id the tool needs, so nothing rides along hidden.
  const handleEditTheme = useCallback(
    (edit: { themeId: string; colors: string[] }) => {
      openChatSidebar();
      // No colon after the chip: chips sit in a flex row in the sent bubble, so punctuation
      // immediately after one reads as a floating ":".
      prefillComposer(
        `Update my ${serializeThemeRef({ themeId: edit.themeId, colors: edit.colors })} to be `
      );
    },
    [openChatSidebar, prefillComposer]
  );
  const handlePrefillConsumed = useCallback(() => {
    setChatPrefillMessage(undefined);
    setChatPrefillContext(undefined);
  }, []);

  // The agent asked for the form (ask_theme_details) rather than interviewing the user.
  // The form opens empty: the agent only reaches for it when the user has NOT said what they
  // want, so any seeded description would just restate their request for them to delete.
  const handleThemeFormRequested = useCallback(() => {
    openChatSidebar();
    setIsThemeRequestOpen(true);
    setThemeRequestFocusNonce(n => n + 1);
    writeThemeRequestDraft(dashboardId, { open: true });
  }, [dashboardId, openChatSidebar]);

  // "+ Custom" in the theme selector: the request form lives in the chat, so open both.
  // Clicking "+ Custom" always resolves to the single card: a fresh form if the last one was
  // already submitted, otherwise the existing draft, scrolled into view and focused either way.
  const handleRequestTheme = useCallback(() => {
    openChatSidebar();
    setIsThemeRequestOpen(true);
    setThemeRequestFocusNonce(n => n + 1);
    if (themeRequestWasSubmittedRef.current) {
      themeRequestWasSubmittedRef.current = false;
      setThemeRequestInstanceId(n => n + 1);
    }
    writeThemeRequestDraft(dashboardId, { open: true });
  }, [dashboardId, openChatSidebar]);

  const handleThemeRequestSubmitted = useCallback(
    (submitted: { userRequestId: string; visibleRequest: string }) => {
      themeRequestWasSubmittedRef.current = true;
      if (!isCustomThemeAgentEnabled) {
        // Gated off: the request was already filed for support; no chat handoff.
        return;
      }
      openChatSidebar();
      // Reads as the user's own ask; the id the tool needs is appended and stripped from the
      // bubble (see stripThemeIdReferences), so no extra card is needed to say what was sent.
      setPendingThemeChatMessage(
        `${submitted.visibleRequest} (theme request id: ${submitted.userRequestId})`
      );
    },
    [isCustomThemeAgentEnabled, openChatSidebar]
  );
  const handleThemeRequestDismissed = useCallback(() => {
    setIsThemeRequestOpen(false);
    clearThemeRequestDraft(dashboardId);
  }, [dashboardId]);
  const handleAutoSubmitConsumed = useCallback(() => setPendingThemeChatMessage(undefined), []);

  // A theme the agent edits in place keeps its `custom:{uuid}` id, so a second result card
  // previews the SAME pointer that is already live: setThemeId bails out and the re-theme effect
  // never sees the freshly registered spec. Bump on every chat-driven theme action to force the
  // re-resolve.
  const [chatThemeSpecNonce, setChatThemeSpecNonce] = useState(0);

  // Chat theme-result card (create_custom_theme replies): preview flips the live theme after
  // snapshotting the current one; Apply persists; Keep current restores the snapshot. The
  // snapshot ref survives re-renders so a preview → keep round-trip lands exactly where it began.
  const liveThemeTrioRef = useRef(liveTheme);
  liveThemeTrioRef.current = liveTheme;
  const chatThemePreviewSnapshotRef = useRef<ThemeTrio | null>(null);
  const handleChatThemePreview = useCallback(
    (themePointer: string) => {
      if (!chatThemePreviewSnapshotRef.current) {
        chatThemePreviewSnapshotRef.current = { ...liveThemeTrioRef.current };
      }
      previewTheme({ themeId: themePointer });
      setChatThemeSpecNonce(n => n + 1);
    },
    [previewTheme]
  );
  const handleChatThemeCancelPreview = useCallback(() => {
    const snapshot = chatThemePreviewSnapshotRef.current;
    if (snapshot) {
      previewTheme(snapshot);
      chatThemePreviewSnapshotRef.current = null;
      setChatThemeSpecNonce(n => n + 1);
    }
  }, [previewTheme]);

  // Chat theme-result card Apply: persist the previewed theme and refresh the selector list.
  const handleChatThemeApply = useCallback(
    (themePointer: string) => {
      chatThemePreviewSnapshotRef.current = null;
      persistThemeRef.current({ themeId: themePointer });
      setChatThemeSpecNonce(n => n + 1);
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_CUSTOM_THEMES]);
    },
    [persistThemeRef, queryClient]
  );

  return {
    isCustomThemeAgentEnabled,
    chatPrefillMessage,
    chatPrefillContext,
    prefillComposer,
    handlePrefillConsumed,
    pendingThemeChatMessage,
    handleAutoSubmitConsumed,
    isThemeRequestOpen,
    themeRequestInstanceId,
    themeRequestFocusNonce,
    handleEditTheme,
    handleThemeFormRequested,
    handleRequestTheme,
    handleThemeRequestSubmitted,
    handleThemeRequestDismissed,
    chatThemeSpecNonce,
    handleChatThemePreview,
    handleChatThemeCancelPreview,
    handleChatThemeApply,
  };
};
